import { clamp } from "@/lib/utils";
import { useCallback, useEffect, useRef } from "react";
interface UseCenterActiveListOptions {
  onCenterChange?: (index: number) => void;
  initial: number;
  axis?: "x" | "y" | "both";
  behavior?: "smooth" | "instant";
}
export function useCenterItem({
  onCenterChange,
  initial,
  behavior = "smooth",
  axis = "both"
}: UseCenterActiveListOptions) {
  // The user has to populate the refs for the scroll container and the items in the list.
  const scrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLElement | null>>([]);
  const activeIndexRef = useRef(initial);
  const setActiveIndex = useCallback((index: number) => {
    activeIndexRef.current = index;
    onCenterChange?.(index);
  }, [onCenterChange]);
  const scrollToIndex = useCallback((index_0: number, b = behavior) => {
    const container = scrollRef.current;
    const item = itemRefs.current.at(clamp(index_0, 0, Math.max(itemRefs.current.length - 1, 0)));
    if (!container || !item) return;
    item.scrollIntoView({
      block: "nearest",
      inline: "center",
      behavior: b
    });
  }, [behavior]);
  const goNext = useCallback(() => {
    scrollToIndex(activeIndexRef.current + 1);
  }, [scrollToIndex]);
  const goPrev = useCallback(() => {
    scrollToIndex(activeIndexRef.current - 1);
  }, [scrollToIndex]);
  useEffect(() => {
    requestAnimationFrame(() => {
      itemRefs.current[activeIndexRef.current]?.scrollIntoView({
        behavior: "instant",
        block: "nearest",
        inline: "center"
      });
    });
  }, []);
  useEffect(() => {
    const container_0 = scrollRef.current;
    if (!container_0) return;
    const rootMargin = {
      x: "0 -45%",
      y: "-45% 0",
      both: "-45% -45%"
    }[axis];
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const index_1 = Number((entry.target as HTMLElement).dataset.index);
          setActiveIndex(index_1);
        }
      }
    }, {
      root: container_0,
      rootMargin,
      threshold: 0
    });
    itemRefs.current?.forEach((child, index_2) => {
      if (!child) return;
      child.dataset.index = String(index_2);
      observer.observe(child);
    });
    return () => observer.disconnect();
  }, [setActiveIndex, axis]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goNext, goPrev]);
  return {
    scrollRef,
    itemRefs,
    activeIndex: activeIndexRef.current,
    scrollToIndex,
    goNext,
    goPrev
  };
}